import {useAnalytics} from '@github-ui/use-analytics'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useCallback, useId, useRef, useState, lazy, Suspense} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {StopIcon} from '@primer/octicons-react'
import {Button, Flash} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {Octicon} from '@primer/react/deprecated'
import type {ChecksSectionStatusType} from '../../helpers/merge-box-status-calculator/checks-section-status'
import {extractChecksAnalyticsMetadata} from '../../helpers/status-check-helpers'
import {useApproveWorkflows} from '../../hooks/use-approve-workflows'
import {useStatusChecksLiveUpdates} from '../../hooks/use-status-checks-live-updates'
import {useSyncFavicon} from '../../hooks/use-sync-favicon'
import {useStatusChecksPageData} from '../../page-data/loaders/use-status-checks-page-data'
import type {MergeBoxUserPreferences} from '../../page-data/payloads/merge-box'
import type {PendingWorkflowApprovalRollup, StatusCheck, StatusRollup} from '../../page-data/payloads/status-checks'
import {accessibleChecksSummary, checksSectionHeading} from './checks/checks-section-header-text'
import {ExpandedChecksList} from './checks/ExpandedChecksList'
import {FailureAnalysisButton} from './checks/FailureAnalysisButton'
import {FailureAnalysisBlankslate} from './checks/FailureAnalysisBlankslate'
import {useCIFindings} from './checks/use-ci-findings'
import {StatusCheckStatesIcon} from './checks/StatusCheckStatesIcon'
import {WorkflowApprovalSubtitle} from './checks/WorkflowApprovalSubtitle'
import {AlertIcon} from './common/AlertIcon'
import {MergeBoxExpandable} from './common/MergeBoxExpandable'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'

const FailureAnalysisPanel = lazy(async () => {
  const m = await import('./checks/FailureAnalysisPanel')
  return {default: m.FailureAnalysisPanel}
})
const CIFindingsBatchProvider = lazy(async () => {
  const m = await import('./checks/CIFindingsBatchContext')
  return {default: m.CIFindingsBatchProvider}
})

export type ChecksSectionProps = {
  pullRequestId: string
  pullRequestHeadSha: string
  focusPrimaryMergeButton: () => void
  sectionStatus: ChecksSectionStatusType
  shouldRender: boolean
  mergeBoxUserPreferences: MergeBoxUserPreferences | null
  viewerCanUseCopilotCodingAgent: boolean
  viewerHasCEOrCBAccess: boolean
}

/**
 * Renders when there are workflows pending approval
 * Conditionally allows the user to approve and run the workflows pending approval
 * Always renders the full list of status checks (does not allow collapse)
 */
function PendingApprovalChecksSection({
  checkSectionAriaId,
  pendingWorkflowApprovalRollup,
  statusRollup,
  statusChecks,
  pullRequestHeadSha,
  pullRequestId,
  focusPrimaryMergeButton,
  mergeBoxUserPreferences,
  viewerCanUseCopilotCodingAgent,
  viewerHasCEOrCBAccess,
}: {
  checkSectionAriaId: string
  pendingWorkflowApprovalRollup: PendingWorkflowApprovalRollup
  statusRollup: StatusRollup
  statusChecks: StatusCheck[]
  pullRequestHeadSha: string
  pullRequestId: string
  focusPrimaryMergeButton: () => void
  mergeBoxUserPreferences: MergeBoxUserPreferences | null
  viewerCanUseCopilotCodingAgent: boolean
  viewerHasCEOrCBAccess: boolean
}) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const {approveWorkflows, isPending} = useApproveWorkflows({
    pullRequestHeadSha,
    onApproved: focusPrimaryMergeButton,
    onError: setErrorMessage,
  })
  const onApproveWorkflows = useCallback(() => {
    setErrorMessage(null)
    approveWorkflows()
  }, [approveWorkflows])
  const {workflowsRequiringApprovalCount, viewerCanApproveWorkflowRuns} = pendingWorkflowApprovalRollup

  const title = `${workflowsRequiringApprovalCount} workflow${
    workflowsRequiringApprovalCount === 1 ? '' : 's'
  } awaiting approval`

  return (
    <>
      <MergeBoxSectionHeader
        headerId={checkSectionAriaId}
        title={title}
        subtitle={<WorkflowApprovalSubtitle pendingWorkflowApprovalRollup={pendingWorkflowApprovalRollup} />}
        icon={<AlertIcon bgColor="var(--bgColor-attention-emphasis)" />}
        rightSideContent={
          viewerCanApproveWorkflowRuns ? (
            <Button loading={isPending} loadingAnnouncement="Re-running workflows" onClick={onApproveWorkflows}>
              Approve workflows to run
            </Button>
          ) : undefined
        }
      />
      {errorMessage && (
        <Flash className="tmp-m-3" variant="danger">
          <Octicon className="mr-2" icon={StopIcon} />
          {errorMessage}
        </Flash>
      )}
      {statusRollup.summary.length > 0 && (
        <MergeBoxExpandable isExpanded>
          <ExpandedChecksList
            pullRequestId={pullRequestId}
            statusChecks={statusChecks}
            statusRollupSummary={statusRollup.summary}
            mergeBoxUserPreferences={mergeBoxUserPreferences}
            viewerCanUseCopilotCodingAgent={viewerCanUseCopilotCodingAgent}
            viewerHasCEOrCBAccess={viewerHasCEOrCBAccess}
          />
        </MergeBoxExpandable>
      )}
    </>
  )
}

/**
 *
 * Renders a collapsible checks section for a pull request
 */
export function ChecksSection({
  pullRequestId,
  pullRequestHeadSha,
  focusPrimaryMergeButton,
  sectionStatus,
  shouldRender,
  mergeBoxUserPreferences,
  viewerCanUseCopilotCodingAgent,
  viewerHasCEOrCBAccess,
}: ChecksSectionProps) {
  const {
    data: {aliveChannels, statusRollup, statusChecks},
  } = useStatusChecksPageData({pullRequestHeadSha})
  // expand checks state by default unless all checks pass or user has already collapsed toggle
  const isPassingChecksState = statusRollup.combinedState === 'PASSED'
  const [checksExpanded, setChecksExpanded] = useSessionStorage<boolean>(
    `${pullRequestId}:checksExpanded`,
    !isPassingChecksState,
  )
  const {sendAnalyticsEvent} = useAnalytics()
  const checkSectionAriaId = useId()
  const [isFailureDialogOpen, setIsFailureDialogOpen] = useState(false)
  const failureAnalysisButtonRef = useRef<HTMLButtonElement>(null)
  const onFailureDialogClose = useCallback(() => setIsFailureDialogOpen(false), [])
  const ciAnalysisEnabled = isFeatureEnabled('ci_analysis_check_runs_ui_enabled')
  // Use statusRollup.combinedState (actual check state) instead of sectionStatus prop,
  // because sectionStatus can be overridden to PENDING_CONFLICTS when there are merge
  // conflicts, masking the real failure state.
  const hasFailures =
    statusRollup.combinedState === 'SOME_FAILED' ||
    statusRollup.combinedState === 'FAILED' ||
    statusRollup.combinedState === 'PENDING_FAILED'
  const showFailureAnalysis = ciAnalysisEnabled && hasFailures
  const {isPolling: ciFindingsPolling, isError: ciFindingsError} = useCIFindings({
    enabled: showFailureAnalysis,
  })

  useStatusChecksLiveUpdates(aliveChannels.commitHeadShaChannel, pullRequestHeadSha)
  useSyncFavicon(statusRollup)

  const workflowApproval = statusRollup.pendingWorkflowApprovalRollup
  if (!shouldRender) return null

  return (
    <>
      <section aria-label="Checks" aria-describedby={checkSectionAriaId} className="border-bottom color-border-subtle">
        {statusRollup.combinedState === 'PENDING_APPROVAL' && workflowApproval ? (
          <PendingApprovalChecksSection
            checkSectionAriaId={checkSectionAriaId}
            pendingWorkflowApprovalRollup={workflowApproval}
            statusRollup={statusRollup}
            statusChecks={statusChecks}
            pullRequestHeadSha={pullRequestHeadSha}
            pullRequestId={pullRequestId}
            focusPrimaryMergeButton={focusPrimaryMergeButton}
            mergeBoxUserPreferences={mergeBoxUserPreferences}
            viewerCanUseCopilotCodingAgent={viewerCanUseCopilotCodingAgent}
            viewerHasCEOrCBAccess={viewerHasCEOrCBAccess}
          />
        ) : (
          <>
            <MergeBoxSectionHeader
              headerId={checkSectionAriaId}
              title={checksSectionHeading[sectionStatus]}
              subtitle={accessibleChecksSummary(statusRollup.summary)}
              icon={<StatusCheckStatesIcon statusRollupSummary={statusRollup.summary} />}
              expandableProps={{
                ariaLabel: checksExpanded ? 'Collapse checks' : 'Expand checks',
                isExpanded: checksExpanded,
                onToggle: () => {
                  const eventMetadata = extractChecksAnalyticsMetadata(statusRollup.summary)
                  const eventTarget = 'MERGEBOX_CHECKS_SECTION_TOGGLE_BUTTON'
                  const eventType = !checksExpanded ? 'checks_section.expand' : 'checks_section.collapse'
                  sendAnalyticsEvent(eventType, eventTarget, eventMetadata)
                  setChecksExpanded(!checksExpanded)
                },
              }}
            />
            <MergeBoxExpandable isExpanded={checksExpanded}>
              <ExpandedChecksList
                statusChecks={statusChecks}
                statusRollupSummary={statusRollup.summary}
                pullRequestId={pullRequestId}
                mergeBoxUserPreferences={mergeBoxUserPreferences}
                viewerCanUseCopilotCodingAgent={viewerCanUseCopilotCodingAgent}
                viewerHasCEOrCBAccess={viewerHasCEOrCBAccess}
              />
            </MergeBoxExpandable>
            {isFailureDialogOpen && (
              <Dialog
                title="Copilot failure analysis"
                onClose={onFailureDialogClose}
                position="right"
                returnFocusRef={failureAnalysisButtonRef}
              >
                <ErrorBoundary
                  boundaryName="MergeBox_FailureAnalysisPanel"
                  fallback={<FailureAnalysisBlankslate status="error" />}
                >
                  <Suspense fallback={<div className="p-3 text-center">Loading…</div>}>
                    <CIFindingsBatchProvider>
                      <FailureAnalysisPanel onApplySuccess={onFailureDialogClose} />
                    </CIFindingsBatchProvider>
                  </Suspense>
                </ErrorBoundary>
              </Dialog>
            )}
          </>
        )}
      </section>
      {showFailureAnalysis && (
        <div className="border-bottom color-border-subtle d-flex p-3">
          <FailureAnalysisButton
            ref={failureAnalysisButtonRef}
            status={ciFindingsError ? 'error' : ciFindingsPolling ? 'loading' : 'ready'}
            onClick={() => setIsFailureDialogOpen(true)}
          />
        </div>
      )}
    </>
  )
}
